import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { EditPage, EditPagePanel, EditPageTextField, EditPageSelect, EditPageCheckbox } from './../../../common/EditPage';
import { useNavigate } from 'react-router-dom';
import { getOne, create } from './../../../common/v4/BackendProvider';
import { toast } from 'react-toastify';
import Select from 'react-select';

const AdminUsersCreate = () => {
    const [data, setData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        timezone: 'UTC',
        enabled: true,
        skip_email_verification: true,
        webrtc_enable: false,
        webrtc_username: '',
        webrtc_password: '',
        access_group_ids: []
    });
    const [metadata, setMetadata] = useState({});
    const navigate = useNavigate();
    
    useEffect(() => {
        getOne('/admin/users/', 'new').then(response => {
            setMetadata(response.data.metadata);
            setData(prevData => ({
                ...prevData,
                timezone: response.data.metadata?.timezones?.default || 'UTC',
            }));
        }).catch(error => {
            toast.error("Error fetching metadata: " + error.message, {});
        });
    }, []);

    const handleSave = () => {
        // Validate required fields
        if (!data.name || !data.email || !data.password) {
            toast.error("Name, email, and password are required", {});
            return;
        }

        // Validate password length
        if (data.password.length < 8) {
            toast.error("Password must be at least 8 characters long", {});
            return;
        }

        create('/admin/users', {data: data}).then(() => {
            toast.success("Admin user created successfully", {});
            navigate('/admin/users');
        }).catch(error => {
            toast.error("Error creating admin user: " + (error?.response?.data?.message || error.message), {});
        });
    };

    const handleCancel = () => {
        navigate('/admin/users');
    };

    const onChange = (id, value) => {
        setData({...data, [id]: value});
    };

    const handleAccessGroupsChange = (selectedOptions) => {
        const groupIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setData({...data, access_group_ids: groupIds});
    };

    // Prepare access group options for react-select
    const accessGroupOptions = metadata?.access_groups?.map(group => ({
        value: group.id,
        label: group.name,
        description: group.description
    })) || [];

    // Prepare timezone options from backend metadata
    const timezoneOptions = metadata?.timezones?.values ? 
        Object.entries(metadata.timezones.values).map(([key, value]) => ({ value: key, label: value })) :
        [{ value: 'UTC', label: 'UTC' }]; // Fallback to just UTC if metadata not loaded

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            <EditPage handleSave={ handleSave } handleCancel={ handleCancel } title="Create Admin User">
                <EditPagePanel className="col-lg-6">
                    <EditPageTextField 
                        label="Name" 
                        id="name" 
                        onChange={ onChange } 
                        value={ data.name }
                        required
                    />
                    <EditPageTextField 
                        label="Email" 
                        id="email" 
                        onChange={ onChange } 
                        value={ data.email }
                        type="email"
                        required
                    />
                    <EditPageTextField 
                        label="Password" 
                        type="password" 
                        id="password" 
                        onChange={ onChange } 
                        value={ data.password }
                        required
                    />
                </EditPagePanel>
                <EditPagePanel className="col-lg-6">
                    <EditPageTextField 
                        label="Phone" 
                        id="phone" 
                        onChange={ onChange } 
                        value={ data.phone }
                    />
                    <EditPageSelect 
                        label="Timezone" 
                        id="timezone" 
                        onChange={ onChange } 
                        value={ data.timezone } 
                        options={ timezoneOptions.reduce((acc, opt) => {
                            acc[opt.value] = opt.label;
                            return acc;
                        }, {}) }
                    />
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: '600',
                            fontSize: '0.875rem'
                        }}>
                            Access Groups
                        </label>
                        <Select
                            isMulti
                            options={accessGroupOptions}
                            value={accessGroupOptions.filter(option => 
                                data.access_group_ids.includes(option.value)
                            )}
                            onChange={handleAccessGroupsChange}
                            placeholder="Select access groups..."
                            isClearable
                            isSearchable
                            formatOptionLabel={(option) => (
                                <div>
                                    <div style={{ fontWeight: '500' }}>{option.label}</div>
                                    {option.description && (
                                        <div style={{ 
                                            fontSize: '0.875rem', 
                                            color: '#666',
                                            fontStyle: 'italic'
                                        }}>
                                            {option.description}
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                    <EditPageCheckbox 
                        label="Enabled" 
                        id="enabled" 
                        onChange={ onChange } 
                        value={ data.enabled } 
                    />
                    <EditPageCheckbox 
                        label="Do not require email verification" 
                        id="skip_email_verification" 
                        onChange={ onChange } 
                        value={ data.skip_email_verification } 
                    />
                </EditPagePanel>
                
                <EditPagePanel className="col-lg-6">
                    <h4 style={{ marginBottom: '1rem', color: '#333' }}>WebRTC Settings</h4>
                    <EditPageCheckbox 
                        label="Enable WebRTC" 
                        id="webrtc_enable" 
                        onChange={ onChange } 
                        value={ data.webrtc_enable } 
                    />
                    {data.webrtc_enable && (
                        <>
                            <EditPageTextField 
                                label="WebRTC Username" 
                                id="webrtc_username" 
                                onChange={ onChange } 
                                value={ data.webrtc_username }
                                placeholder="Enter WebRTC username"
                            />
                            <EditPageTextField 
                                label="WebRTC Password" 
                                type="password" 
                                id="webrtc_password" 
                                onChange={ onChange } 
                                value={ data.webrtc_password }
                                placeholder="Enter WebRTC password"
                            />
                        </>
                    )}
                </EditPagePanel>
            </EditPage>
        </>
    );
};

export default AdminUsersCreate;
